import type { EntityData } from '@adornis/baseql/entities/types.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { Permission } from '../../permissions/roles.js';
import type { Person } from '../entities/person.js';
import type { SchuelerDaten } from '../students/schueler-daten.js';

export type InviteType = 'Sorgeberechtigter' | 'Spender';

export interface InvitationOptions {
  invitePermission: Permission;
  manualConfirmPermission: Permission;
  removePermission: Permission;
  schuelerInviteField: keyof Pick<EntityData<SchuelerDaten>, 'eingeladeneSorgeberechtigte' | 'eingeladeneSpender'>;
  sbInviteField: keyof Pick<EntityData<Person>, 'sorgeberechtigteKinderIDs' | 'spendenKinderIDs'>;
}

export const InvitationOptionsForType: Record<InviteType, InvitationOptions> = {
  Sorgeberechtigter: {
    invitePermission: Permission.INVITE_SORGEBERECHTIGTE,
    manualConfirmPermission: Permission.MANUAL_SORGEBERECHTIGTE_CONFIRM,
    removePermission: Permission.REMOVE_SORGEBERECHTIGTE,
    schuelerInviteField: 'eingeladeneSorgeberechtigte',
    sbInviteField: 'sorgeberechtigteKinderIDs',
  },
  Spender: {
    invitePermission: Permission.INVITE_SPENDER,
    manualConfirmPermission: Permission.MANUAL_SPENDER_CONFIRM,
    removePermission: Permission.REMOVE_SPENDER,
    schuelerInviteField: 'eingeladeneSpender',
    sbInviteField: 'spendenKinderIDs',
  },
};

/**
 * Sorgeberechtigter and Spender exclude each other, whatever child: a person linked one way is never linked the other
 * way as well.
 * @param movingChildID the child a toggle moves over to `inviteType` - its old link does not count
 */
export function assertSingleRelationshipType(
  person: Pick<EntityData<Person>, 'sorgeberechtigteKinderIDs' | 'spendenKinderIDs'>,
  inviteType: InviteType,
  movingChildID?: string,
) {
  const otherType: InviteType = inviteType === 'Sorgeberechtigter' ? 'Spender' : 'Sorgeberechtigter';
  const otherLinks = person[InvitationOptionsForType[otherType].sbInviteField]?.filter(id => id !== movingChildID);
  if (otherLinks?.length) {
    throw new ExpectedError(`Die Person ist bereits ${otherType} und kann nicht zusätzlich ${inviteType} werden.`);
  }
}
